import { ReceivableItem, InstallmentItem, DisbursementItem } from '../../types/operations';

/**
 * Maps a receivable item from API response to internal format.
 * Handles field name differences between API and frontend.
 */
export const mapReceivableFromApi = (item: any): ReceivableItem => ({
    person_id: item.person_id || '',
    receivable_id: item.receivable_id || '',
    cfop: item.cfop || '',
    issue_date: item.issue_date || item.document_date || '',
    debtor_name: item.debtor_name || '',
    document_id: item.document_id || item.document_number || '',
    address_city: item.address_city || '',
    debtor_email: item.debtor_email || '',
    address_phone: item.address_phone || '',
    address_state: item.address_state || '',
    assignor_cnpj: item.assignor_cnpj || item.assignor_document || '',
    assignor_name: item.assignor_name || '',
    address_number: item.address_number || '',
    address_street: item.address_street || '',
    document_title: item.document_title || item.document_type || '',
    xml_size_bytes: item.xml_size_bytes || 0,
    address_country: item.address_country || '',
    debtor_cpf_cnpj: item.debtor_cpf_cnpj || item.debtor_document || '',
    address_district: item.address_district || '',
    address_zip_code: item.address_zip_code || '',
    receivable_v_pag: item.receivable_v_pag || '',
    address_ibge_code: item.address_ibge_code || '',
    receivable_amount: Number.parseFloat(item.receivable_amount || item.receivable_value || '0') || 0,
    receivable_nat_op: item.receivable_nat_op || '',
    receivable_number: item.receivable_number || item.document_number || '',
    address_complement: item.address_complement || '',
    receivable_ind_pag: item.receivable_ind_pag || '',
    receivable_due_date: item.receivable_due_date || '',
    disbursement_value: Number.parseFloat(item.disbursement_value || '0'),
    fee_name: item.fee_name || '',
    fee_value: Number.parseFloat(item.fee_value || '0'),
    guarantee_percent: Number.parseFloat(item.guarantee_percent || '0'),
    main_risk: item.main_risk || '',
    external_link: item.external_link || '',
    operation_date: item.operation_date || '',
    present_value: Number.parseFloat(item.present_value || '0'),
});

/**
 * Maps a receivable item to the API payload format.
 */
export const mapReceivableToPayload = (item: ReceivableItem) => ({
    person_id: item.person_id || '',
    receivable_id: item.receivable_id || '',
    document_id: item.document_id || '',
    document_title: item.document_title || '',
    cfop: item.cfop || '',
    issue_date: item.issue_date || '',
    xml_size_bytes: item.xml_size_bytes || 0,
    assignor_name: item.assignor_name || '',
    assignor_cnpj: item.assignor_cnpj || '',
    debtor_cpf_cnpj: item.debtor_cpf_cnpj || '',
    debtor_name: item.debtor_name || '',
    debtor_email: item.debtor_email || '',
    address_street: item.address_street || '',
    address_number: item.address_number || '',
    address_complement: item.address_complement || '',
    address_district: item.address_district || '',
    address_ibge_code: item.address_ibge_code || '',
    address_city: item.address_city || '',
    address_state: item.address_state || '',
    address_zip_code: item.address_zip_code || '',
    address_country: item.address_country || '',
    address_phone: item.address_phone || '',
    receivable_number: item.receivable_number || '',
    receivable_due_date: item.receivable_due_date || '',
    receivable_amount: item.receivable_amount || 0,
    receivable_v_pag: item.receivable_v_pag || '',
    receivable_nat_op: item.receivable_nat_op || '',
    receivable_ind_pag: item.receivable_ind_pag || '',
    disbursement_value: item.disbursement_value || 0,
    fee_name: item.fee_name || '',
    fee_value: item.fee_value || 0,
    guarantee_percent: item.guarantee_percent || 0,
    main_risk: item.main_risk || '',
    external_link: item.external_link || '',
    operation_date: item.operation_date || '',
    present_value: item.present_value || 0,
});

/**
 * Maps receivables data from API schema (handles both 'duplicates' and 'receivables' keys).
 */
export const mapReceivablesFromSchema = (schema: any) => {
    const duplicates = schema?.duplicates;
    const receivables = schema?.receivables;
    const source =
        (duplicates?.items?.length ? duplicates : receivables) ||
        duplicates ||
        receivables ||
        {};
    return {
        quantity: source?.quantity || 0,
        total: source?.total || 0,
        items: (source?.items || []).map(mapReceivableFromApi),
        overcollateral: receivables?.overcollateral ?? null,
    };
};

/**
 * Maps an installment to API payload format (date DD/MM/YYYY → YYYY-MM-DD).
 */
export const mapInstallmentToPayload = (inst: InstallmentItem) => ({
    number: inst.number,
    value: inst.value || 0,
    dueDate: inst.dueDate ? inst.dueDate.split('/').reverse().join('-') : '',
    format: inst.format || 'VALOR',
    operation_date: inst.operation_date ? inst.operation_date.split('/').reverse().join('-') : '',
    present_value: inst.present_value || 0,
    person_id: inst.person_id || '',
    business_reference: inst.business_reference || '',
    document_id: inst.document_id || '',
    document_title: inst.document_title || '',
    receivable_installment: inst.receivable_installment || '',
    debtor_name: inst.debtor_name || '',
    debtor_cpf_cnpj: inst.debtor_cpf_cnpj || '',
    debtor_address_street: inst.debtor_address_street || '',
    debtor_address_number: inst.debtor_address_number || '',
    debtor_address_complement: inst.debtor_address_complement || '',
    debtor_address_district: inst.debtor_address_district || '',
    debtor_address_city: inst.debtor_address_city || '',
    debtor_address_state: inst.debtor_address_state || '',
    debtor_address_zip_code: inst.debtor_address_zip_code || '',
    debtor_address_ibge_code: inst.debtor_address_ibge_code || '',
});

/**
 * Builds the installment section for the API payload.
 */
export const buildInstallmentPayload = (installments: InstallmentItem[]) => {
    if (!installments.length) {
        return {
            total: 0,
            lastDueDate: '',
            items: [],
        };
    }

    const safeInstallments = installments.filter(inst => inst.dueDate);

    const lastDueDate =
        safeInstallments.length > 0
            ? [...safeInstallments]
                .sort(
                    (a, b) =>
                        new Date(b.dueDate.split('/').reverse().join('-')).getTime() -
                        new Date(a.dueDate.split('/').reverse().join('-')).getTime()
                )[0]
                .dueDate.split('/')
                .reverse()
                .join('-')
            : '';

    return {
        total: installments.reduce((sum, inst) => sum + inst.value, 0),
        lastDueDate,
        items: installments.map(mapInstallmentToPayload),
    };
};

/**
 * Maps a disbursement to API payload format.
 */
export const mapDisbursementToPayload = (d: DisbursementItem) => ({
    amount: d.amount || 0,
    date: d.date ? d.date.split('/').reverse().join('-') : '',
});
